import { formatDate } from '@fullcalendar/core';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicationIcon from '@mui/icons-material/Medication';
import PersonIcon from '@mui/icons-material/Person';
import { Alert, Box, Card, CardContent, CardHeader, Chip, Grid, Typography } from '@mui/material';
import { useRedirect } from 'react-admin';
import { useLocation } from 'react-router-dom';
import {
  DetectedDocumentResponse,
  DocumentDetailResponse,
  PatientDocumentResponse,
  PriorAuthDocumentResponse,
} from '../../../../interfaces/document';
import { PageHeader } from '../../../common/PageHeader';
import FieldDisplay from '../../FieldDisplay';
import { getStatusColorStyles } from '../../shared';
import { getDocumentsLibraryUrlWithFilters } from '../../utils/navigationHelpers';

interface PriorAuthDocumentsTabProps {
  document: DocumentDetailResponse;
}

interface CombinedPriorAuthData {
  priorAuthDocument: PriorAuthDocumentResponse;
  patientDocument: PatientDocumentResponse;
  detectedDocument: DetectedDocumentResponse;
  extractedData: Record<string, string>;
  fieldConfidence: Record<string, number>;
}

export const PriorAuthDocumentsTab: React.FC<PriorAuthDocumentsTabProps> = ({ document }) => {
  const redirect = useRedirect();
  const location = useLocation();

  const getCombinedPriorAuthData = (): CombinedPriorAuthData[] => {
    return (
      document.priorAuthDocuments?.map((priorAuthDoc) => {
        let patientDocument = null;
        let detectedDocument = null;
        let extractedData = null;
        let fieldConfidence = null;

        for (const dd of document.detectedDocuments || []) {
          for (const pd of dd.patientDocuments || []) {
            if (pd.priorAuthDocument?.id === priorAuthDoc.id) {
              patientDocument = pd;
              detectedDocument = dd;
              extractedData = pd.extractedData || {};
              fieldConfidence = extractedData.field_confidence || {};
              break;
            }
          }
          if (patientDocument) {
            break;
          }
        }

        return {
          priorAuthDocument: priorAuthDoc as PriorAuthDocumentResponse,
          patientDocument: patientDocument as PatientDocumentResponse,
          detectedDocument: detectedDocument as DetectedDocumentResponse,
          extractedData: extractedData || {},
          fieldConfidence: fieldConfidence || {},
        };
      }) || []
    );
  };

  const getPaStatusDisplay = (status?: string): string => {
    if (!status) {
      return 'Unknown';
    }

    switch (status.toLowerCase()) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'denied':
        return 'Denied';
      case 'partially-approved':
        return 'Partially Approved';
      case 'expired':
        return 'Expired';
      case 'under-review':
        return 'Under Review';
      case 'more-info-required':
        return 'More Info Required';
      default:
        return status;
    }
  };

  const getPaStatusColor = (status?: string) => {
    if (!status) {
      return {};
    }

    switch (status.toLowerCase()) {
      case 'approved':
        return {
          bgcolor: 'success.light',
          color: 'success.contrastText',
        };
      case 'denied':
        return {
          bgcolor: 'error.light',
          color: 'error.contrastText',
        };
      case 'pending':
      case 'under-review':
        return {
          bgcolor: 'warning.light',
          color: 'warning.contrastText',
        };
      case 'expired':
      case 'more-info-required':
        return {
          bgcolor: 'grey.400',
          color: 'grey.contrastText',
        };
      default:
        return {};
    }
  };

  const formatMedicationList = (medications?: any[]): string => {
    if (!medications || medications.length === 0) return '';
    return medications.map((med) => med.medicationName || 'Unknown medication').join(', ');
  };

  const combinedPriorAuth = getCombinedPriorAuthData();

  if (combinedPriorAuth.length === 0) {
    return (
      <Alert severity='info'>
        No prior authorization documents found. Prior authorizations are created when the system
        detects prior-authorization-type documents with patient and medication information.
      </Alert>
    );
  }

  return (
    <Box>
      <PageHeader
        showBackButton={true}
        onBack={() => redirect(getDocumentsLibraryUrlWithFilters(location.search))}
        backButtonLabel='Back to Library'
        breadcrumbs={[
          {
            label: 'Documents',
            onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
          },
          {
            label: 'Library',
            onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
          },
          { label: `${document.externalId || document.documentId}`, active: true },
        ]}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant='h6'>
          Prior Authorization Documents ({combinedPriorAuth.length})
        </Typography>
      </Box>

      {combinedPriorAuth.map(
        ({ priorAuthDocument, patientDocument, detectedDocument, extractedData }) => {
          const patientName =
            priorAuthDocument.patient?.firstName && priorAuthDocument.patient?.lastName
              ? `${priorAuthDocument.patient.firstName} ${priorAuthDocument.patient.lastName}`
              : priorAuthDocument.patient?.patientId ||
                patientDocument?.patientIdentifier ||
                'Unknown Patient';

          return (
            <Card key={priorAuthDocument.id} variant='outlined' sx={{ mb: 3 }}>
              <CardHeader
                avatar={<MedicationIcon color='primary' />}
                title={<Typography variant='h6'>{patientName}</Typography>}
                subheader={
                  <Box>
                    {detectedDocument && (
                      <Typography variant='body2' color='text.secondary'>
                        Document: {detectedDocument.documentType} • Pages{' '}
                        {detectedDocument.pageStart}-{detectedDocument.pageEnd} •
                        {((detectedDocument.confidence || 0) * 100).toFixed(1)}% confidence
                      </Typography>
                    )}
                    {priorAuthDocument.formSubmissionTrackingKey && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                        sx={{ mt: 0.5 }}>
                        Tracking: {priorAuthDocument.formSubmissionTrackingKey}
                      </Typography>
                    )}
                  </Box>
                }
              />

              {/* Status Information Banner - Only document status and creation date */}
              <Box
                sx={{
                  mx: 2,
                  mt: 1,
                  mb: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='body2' color='text.secondary' fontWeight='500'>
                      Status:
                    </Typography>
                    <Chip
                      label={priorAuthDocument.status}
                      size='small'
                      variant='outlined'
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        ...getStatusColorStyles(priorAuthDocument.status),
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='body2' color='text.secondary' fontWeight='500'>
                      Created:
                    </Typography>
                    <Typography variant='body2' color='text.primary'>
                      {formatDate(priorAuthDocument.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Prior Authorization Details - Prominent section */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}>
                    <MedicationIcon sx={{ mr: 1 }} color='primary' />
                    <Typography variant='h6'>Prior Authorization Details</Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                        sx={{ mb: 1, fontWeight: 500 }}>
                        PA Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
                        <Chip
                          label={getPaStatusDisplay(priorAuthDocument.paStatus)}
                          size='medium'
                          variant='filled'
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            height: '32px',
                            ...getPaStatusColor(priorAuthDocument.paStatus),
                          }}
                        />
                      </Box>
                    </Grid>
                    {priorAuthDocument.paPlatform && (
                      <FieldDisplay
                        borderColor='grey.200'
                        label='Platform'
                        value={priorAuthDocument.paPlatform}
                        gridSize={{ xs: 12, md: 4 }}
                      />
                    )}
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Submission Date'
                      value={
                        priorAuthDocument.documentSubmissionDate
                          ? formatDate(priorAuthDocument.documentSubmissionDate)
                          : null
                      }
                      gridSize={{ xs: 12, md: 4 }}
                    />
                    <Grid item xs={6}>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                        sx={{ mb: 0.5, fontWeight: 400 }}>
                        Form Submission URL
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 32,
                          p: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}>
                        <Typography
                          variant='body2'
                          component='a'
                          href={priorAuthDocument.formSubmissionUrl || '#'}
                          target='_blank'
                          rel='noopener noreferrer'
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            wordBreak: 'break-all',
                          }}>
                          {priorAuthDocument.formSubmissionUrl}
                        </Typography>
                      </Box>
                    </Grid>
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Tracking Key'
                      value={priorAuthDocument.formSubmissionTrackingKey}
                      gridSize={{ xs: 12, md: 6 }}
                    />
                  </Grid>
                </Box>

                {/* Patient Information */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}>
                    <PersonIcon sx={{ mr: 1 }} color='primary' />
                    <Typography variant='h6'>Patient Information</Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <FieldDisplay
                      borderColor='grey.200'
                      label='First Name'
                      value={priorAuthDocument.patient?.firstName}
                      gridSize={{ xs: 12, md: 3 }}
                    />
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Last Name'
                      value={priorAuthDocument.patient?.lastName}
                      gridSize={{ xs: 12, md: 3 }}
                    />
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Date of Birth'
                      value={priorAuthDocument.patient?.dateOfBirth}
                      gridSize={{ xs: 6, md: 3 }}
                    />
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Patient ID'
                      value={priorAuthDocument.patient?.patientId}
                      gridSize={{ xs: 6, md: 3 }}
                    />
                  </Grid>
                </Box>

                {/* Provider Information */}
                {priorAuthDocument.provider && (
                  <Box sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        pb: 1,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}>
                      <LocalHospitalIcon sx={{ mr: 1 }} color='primary' />
                      <Typography variant='h6'>Provider Information</Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <FieldDisplay
                        borderColor='grey.200'
                        label='Provider Name'
                        value={priorAuthDocument.provider.name}
                        gridSize={{ xs: 12, md: 4 }}
                      />
                      <FieldDisplay
                        borderColor='grey.200'
                        label='Phone'
                        value={priorAuthDocument.provider.phone}
                        gridSize={{ xs: 6, md: 4 }}
                      />
                      <FieldDisplay
                        borderColor='grey.200'
                        label='Fax'
                        value={priorAuthDocument.provider.fax}
                        gridSize={{ xs: 6, md: 4 }}
                      />
                    </Grid>
                  </Box>
                )}

                {/* Medications */}
                {priorAuthDocument.medications && priorAuthDocument.medications.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        pb: 1,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}>
                      <MedicationIcon sx={{ mr: 1 }} color='primary' />
                      <Typography variant='h6'>Medications</Typography>
                    </Box>
                    {priorAuthDocument.medications.map((medication, index) => (
                      <Box key={index} sx={{ mb: 3 }}>
                        <Typography
                          variant='subtitle2'
                          sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                          Medication {index + 1}
                        </Typography>
                        <Grid container spacing={3}>
                          <FieldDisplay
                            borderColor='grey.200'
                            label='Medication Name'
                            value={medication.medicationName}
                            gridSize={{ xs: 12, md: 6 }}
                          />
                          <FieldDisplay
                            borderColor='grey.200'
                            label='Dosage'
                            value={medication.medicationDosage}
                            gridSize={{ xs: 6, md: 3 }}
                          />
                          <FieldDisplay
                            borderColor='grey.200'
                            label='Duration'
                            value={medication.medicationDuration}
                            gridSize={{ xs: 6, md: 3 }}
                          />
                          {medication.relatedDiagnosisIcd10Codes &&
                            medication.relatedDiagnosisIcd10Codes.length > 0 && (
                              <FieldDisplay
                                borderColor='grey.200'
                                label='Related ICD-10 Codes'
                                value={medication.relatedDiagnosisIcd10Codes.join(', ')}
                                gridSize={{ xs: 12, md: 12 }}
                              />
                            )}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Diagnoses */}
                {priorAuthDocument.diagnoses && priorAuthDocument.diagnoses.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        pb: 1,
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                      }}>
                      <AssignmentIcon sx={{ mr: 1 }} color='primary' />
                      <Typography variant='h6'>Diagnoses</Typography>
                    </Box>
                    {priorAuthDocument.diagnoses.map((diagnosis, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography
                          variant='subtitle2'
                          sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                          Diagnosis {index + 1}
                        </Typography>
                        <Grid container spacing={3}>
                          <FieldDisplay
                            borderColor='grey.200'
                            label='ICD-10 Code'
                            value={diagnosis.diagnosisIcd10Code}
                            gridSize={{ xs: 12, md: 3 }}
                          />
                          <FieldDisplay
                            borderColor='grey.200'
                            label='Description'
                            value={diagnosis.diagnosisDescription}
                            gridSize={{ xs: 12, md: 9 }}
                          />
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Clinical Information */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}>
                    <AssignmentIcon sx={{ mr: 1 }} color='primary' />
                    <Typography variant='h6'>Clinical Information</Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Clinical Rationale'
                      value={priorAuthDocument.clinicalRationale}
                      gridSize={{ xs: 12, md: 12 }}
                    />
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Coverage Start Date'
                      value={priorAuthDocument.coverage?.startDate}
                      gridSize={{ xs: 6, md: 3 }}
                    />
                    <FieldDisplay
                      borderColor='grey.200'
                      label='Coverage End Date'
                      value={priorAuthDocument.coverage?.endDate}
                      gridSize={{ xs: 6, md: 3 }}
                    />
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          );
        },
      )}
    </Box>
  );
};
